import { Text, Link, Table, Box } from "@radix-ui/themes";
import ReactMarkdown from "react-markdown";
import { rewriteUrl } from "~/utils/rewrite-news-urls";
import NewsPopover from "~/components/news-popover";
import MobileNewsModal from "~/components/demo/mobile-news-modal";
import { ReactNode } from "react";
import { jsonrepair } from "jsonrepair";
import { CopyButton } from "~/components/copy-button";

interface SecurityLinkProps {
  href?: string;
  enableCopy?: boolean;
  children: ReactNode;
}

type BulletizeResponse = {
  hook?: string[];
  negative_highlights?: string[];
  positive_highlights?: string[];
  [key: string]: string[] | undefined;
};

const isBulletizeResponse = (
  content: string | BulletizeResponse
): content is BulletizeResponse => {
  return (
    typeof content === "object" && content !== null && !Array.isArray(content)
  );
};

const parseMalformedJson = (jsonString: string): BulletizeResponse | null => {
  try {
    return JSON.parse(jsonString);
  } catch {
    try {
      const repaired = jsonrepair(jsonString);
      return JSON.parse(repaired);
    } catch (error) {
      console.warn(
        "Failed to parse malformed JSON with jsonrepair:",
        jsonString,
        error
      );
      return null;
    }
  }
};

const processContent = (
  content: string | BulletizeResponse
): BulletizeResponse | string => {
  if (typeof content === "object") {
    return content;
  }

  const trimmed = content.trim();
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    const parsed = parseMalformedJson(trimmed);
    if (parsed) {
      return parsed;
    }
  }

  return content;
};

interface KeyContributor {
  name: string;
  description: string;
}

interface FundContent {
  overall: string;
  keyContributors: KeyContributor[];
}

const parseKeyContributors = (content: string): FundContent | null => {
  // Check if content contains "key contributors" section
  if (!content.toLowerCase().includes("key contributors")) {
    return null;
  }

  const parts = content.split(/\n\nkey contributors/i);
  const overall = parts[0].trim();

  const securitiesSection = (parts[1] || "").trim();

  const lines = securitiesSection
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const keyContributors: KeyContributor[] = [];

  for (let i = 0; i < lines.length; i += 2) {
    const name = lines[i];
    const description = lines[i + 1] || "";

    if (name) {
      keyContributors.push({ name, description });
    }
  }

  return { overall, keyContributors };
};

const returnMarkdown = (raw: string): string => {
  // If content contains tab-indented bullets, treat it as markdown directly
  if (typeof raw === "string" && raw.includes("\n\t-")) {
    return raw.replace(/\n\t-\s*/g, "\n- ");
  }

  const processed = processContent(raw);
  if (!isBulletizeResponse(processed)) {
    if (typeof processed === "string") {
      return processed;
    }
    return processed;
  }
  let content = "";
  const formatSectionTitle = (key: string): string => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  Object.entries(processed)
    .reverse()
    .forEach(([key, items]) => {
      if (items && items.length > 0) {
        content += `**${formatSectionTitle(key)}:**\n\n`;
        items.forEach((item) => {
          content += `• ${item}\n\n`;
        });
        content += "\n";
      }
    });

  return content.trim();
};

export default function SecurityExplainContent({
  content,
  popoutNews = false,
  enableCopy = false,
  isMobileView = false,
  newsProvider = "mtnews",
  newsTruncateWords,
  newsLinkText,
}: {
  content: string;
  popoutNews?: boolean;
  enableCopy?: boolean;
  isMobileView?: boolean;
  newsProvider?: string;
  newsTruncateWords?: number;
  newsLinkText?: string;
}) {
  // Check if content has key contributors format
  const fundContent = parseKeyContributors(content);
  const markdownContent = returnMarkdown(content);

  const renderLink = (linkProps: SecurityLinkProps) => {
    const href = linkProps.href || "";
    const linkText = linkProps.children;
    const linkStyle = {
      color: "var(--demo-blue-color, #4095A8)",
      textDecoration: "underline",
    };

    if (popoutNews) {
      if (isMobileView) {
        return (
          <MobileNewsModal url={href}>
            <Link className="cursor-pointer" style={linkStyle}>
              {linkText}
            </Link>
          </MobileNewsModal>
        );
      } else {
        return (
          <NewsPopover
            url={href}
            provider={newsProvider}
            collisionPadding={16}
            avoidCollisions={true}
            maxWidth="750px"
            truncateWords={newsTruncateWords}
            linkText={newsLinkText}
          >
            <Link className="cursor-pointer" style={linkStyle}>
              {linkText}
            </Link>
          </NewsPopover>
        );
      }
    }
    return (
      <Link
        {...linkProps}
        href={rewriteUrl(href, false, newsProvider)}
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyle}
      />
    );
  };

  if (fundContent) {
    return (
      <Box>
        <ReactMarkdown
          components={{
            a: renderLink,
          }}
        >
          {fundContent.overall}
        </ReactMarkdown>

        <Box mt="4">
          <Text size="3" weight="bold" mb="2" style={{ display: "block" }}>
            Key Contributors
          </Text>
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Security</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Explanation</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {fundContent.keyContributors.map((contributor, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <Text weight="bold">{contributor.name}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <ReactMarkdown
                      components={{
                        a: renderLink,
                        p: ({ children }) => <Text size="2">{children}</Text>,
                      }}
                    >
                      {contributor.description}
                    </ReactMarkdown>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>

        {enableCopy && (
          <CopyButton content={markdownContent} newsProvider={newsProvider} />
        )}
      </Box>
    );
  }

  return (
    <Text suppressHydrationWarning>
      <ReactMarkdown
        components={{
          ul: ({ children }) => (
            <ul
              style={{
                listStyleType: "disc",
                paddingLeft: "1.5rem",
                marginBottom: "1rem",
              }}
            >
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol
              style={{
                listStyleType: "decimal",
                paddingLeft: "1.5rem",
                marginBottom: "1rem",
              }}
            >
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li style={{ marginBottom: "0.5rem" }}>{children}</li>
          ),
          a: renderLink,
        }}
      >
        {returnMarkdown(content)}
      </ReactMarkdown>

      {enableCopy && (
        <CopyButton content={markdownContent} newsProvider={newsProvider} />
      )}
    </Text>
  );
}
